import { AppShell, Container } from '@mantine/core';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  useBot,
  useCollaborateStore,
  useMedia,
  changeCurrentUserPassword,
  isAgentRobot,
  isNewAgentType,
  registerCompanyIdGetter,
} from '@mygpt/app';
import { getPasswordPolicyErrorMessage } from '@mygpt/app/utils';

import AdminHeader from '@/components/Headers/AdminHeader';
import Navbar from '@/components/Navbar';
import Notification from '@/components/Notification';
import { NotificationContext, visitorListItem } from '@/components/Notification/hooks';
import FallBack from '@/components/SuspenseFallBack';
import { assistLinks, getBotLinks, navLinks, assistToolLinks, botToolLinks } from '@/config/links';
import { useHomepageTemplate } from '@/hooks/useHomepageTemplate';
import {
  SvgRealtime,
  SvgRealtimeFilled,
  SvgPhoneReception,
  SvgPhoneReceptionFilled,
  SvgTicket,
  SvgTicketFilled,
} from '@/components/Navbar/icon';
import { useGPTStore } from '@/store';
import { useNavStore } from '@/store/nav';
import { useAdminStore } from '@/store/admin';
import ChangePasswordModal from '@/components/AdminConsole/components/ChangePasswordModal';
import classes from './index.module.css';
import { showNotification } from '@mantine/notifications';
import { NavItem } from '@/types/nav-item';
import { EnableOrganizationView } from '@/pages/NewAdminConsole/components/EnableOrganizationView';
import { DisabledUserView } from '@/pages/NewAdminConsole/components/DisabledUserView';
// import { useSubscriptionStore } from '@mygpt/app/subscription/store';

export default ({ children }: PropsWithChildren) => {
  const { botId } = useBot();
  const { navbarOpened } = useNavStore();
  const { t } = useTranslation();
  const { fetchUserInfo, robotInfo, getConfigInfo, getQuotaInfo, assetScope } = useGPTStore((state) => state);
  const { fetchCompanyId, companyId, hasAdminConsoleAuthority, needsDefaultPasswordChange, userOrgEnabled } =
    useAdminStore((state) => state);
  const { isAssist } = useCollaborateStore();

  const [loading, setLoading] = useState(true);
  const [visitorList, setVisitorList] = useState<visitorListItem[]>([]);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  const { isMobile } = useMedia();
  const location = useLocation();

  // 企业模式下未加入企业时，以下个人页面不应被拦截
  const orgBypassPaths = ['/account', '/message-center', '/keys'];
  const isOrgBypassRoute = useMemo(
    () => orgBypassPaths.some((path) => location.pathname.startsWith(path)),
    [location.pathname]
  );

  // 合并相同group的NavItem[]
  const mergeNavItems = (links1: NavItem[], links2: NavItem[]): NavItem[] => {
    const result = [...links2];

    links1.forEach((link1) => {
      const existingIndex = result.findIndex((r) => r.group === link1.group);
      if (existingIndex >= 0) {
        // 合并item数组，将新items添加到现有items后面
        result[existingIndex] = {
          ...result[existingIndex],
          item: [...result[existingIndex].item, ...link1.item],
        };
      } else {
        // 添加新的group
        result.push(link1);
      }
    });

    return result;
  };

  const hasHomepageTemplate = useHomepageTemplate(isAssist ? undefined : botId);
  const navData = botId ? (isAssist ? assistLinks : getBotLinks(hasHomepageTemplate)) : navLinks;

  const isLocalMode = import.meta.env.VITE_MODE === 'local';
  // Cloud 模式下用户在企业中被禁用
  const isUserDisabledInOrg = !isLocalMode && assetScope !== null && !!companyId && userOrgEnabled === false;
  const isNewAgent = isNewAgentType(robotInfo?.robot_type);
  const isAgent = isAgentRobot(robotInfo?.robot_type);
  // 检查是否禁用工具菜单，默认为false（显示工具菜单）
  const isToolMenuDisabled = (window as any).ENV?.GBASE_SUPPORT_FRONTEND_AGENT_TOOL_MENU_DISABLE === 'true';

  let combinedNavData = navData;

  // 添加工具菜单（针对new agent类型，仅在有botId时显示）
  if (botId && isNewAgent && !isToolMenuDisabled) {
    combinedNavData = isAssist ? [...assistToolLinks, ...combinedNavData] : [...botToolLinks, ...combinedNavData];
  }

  // 添加实时对话菜单（针对非local模式的agent类型）
  if (!isLocalMode && isAgent && botId) {
    const realtimeLinks: NavItem[] = [
      {
        group: 'menu.bot.workshop',
        item: [
          {
            label: 'menu.bot.realtime',
            link: `/bot/${botId}/realtime`,
            icon: SvgRealtime,
            activeIcon: SvgRealtimeFilled,
            showBeta: true,
          },
          {
            label: 'menu.bot.phone',
            link: `/bot/${botId}/phone`,
            icon: SvgPhoneReception,
            activeIcon: SvgPhoneReceptionFilled,
            showBeta: true,
          },
        ],
      },
    ];
    combinedNavData = mergeNavItems(realtimeLinks, combinedNavData);
  }
  // 問い合わせ連携（Zendesk 回复环運営画面）：任意 bot 类型，本地模式除外；灰度用 HIDE_ZENDESK（与連携アプリ卡同一开关）
  if (!isLocalMode && botId && window.ENV?.GBASE_SUPPORT_FRONTEND_HIDE_ZENDESK !== 'true') {
    const zendeskLinks: NavItem[] = [
      {
        group: 'menu.bot.workshop',
        item: [
          {
            label: 'menu.bot.zendesk',
            link: `/bot/${botId}/zendesk`,
            icon: SvgTicket,
            activeIcon: SvgTicketFilled,
            showBeta: true,
          },
        ],
      },
    ];
    combinedNavData = mergeNavItems(zendeskLinks, combinedNavData);
  }
  const handlePasswordChange = async (oldPassword: string, newPassword: string) => {
    try {
      const { data } = await changeCurrentUserPassword({ oldPassword, newPassword });
      if (data.success) {
        showNotification({
          message: t('adminConsole.tip.update_success'),
          color: 'green',
        });
        return { success: true };
      } else {
        return {
          success: false,
          error: t(`adminConsole.tip.${data.error.name}`),
        };
      }
    } catch (error) {
      // 新错误码分支:PASSWORD_POLICY_VIOLATION / PASSWORD_REUSED(detail.code 判别)
      return {
        success: false,
        error: getPasswordPolicyErrorMessage(error, t) || t('adminConsole.employees.passwordChangeFailed'),
      };
    }
  };

  useEffect(() => {
    // 注册 assetScope 获取函数，用于请求拦截器自动添加 company_id 参数
    // assetScope 为 null 表示个人模式，有值表示组织模式
    registerCompanyIdGetter(() => useGPTStore.getState().assetScope);

    Promise.all([fetchUserInfo(), getConfigInfo(), getQuotaInfo()]).finally(() => {
      setLoading(false);
    });

    // Cloud 模式下检查未读消息
    if (import.meta.env.VITE_MODE !== 'local') {
      useAdminStore.getState().fetchUnreadMessageCount();
    }

    // 只在Main页面未初始化时调用，避免重复
    const { isDataReady, isDataLoading } = useAdminStore.getState();
    if (!isDataReady && !isDataLoading) {
      console.log('[Layout] Initializing company data...');
      // 使用 fetchAllAdminData 替代 fetchCompanyId，避免重复调用
      useAdminStore
        .getState()
        .fetchAllAdminData()
        .catch((error) => {
          console.error('[Layout] Failed to initialize admin data:', error);
        });
    }
  }, []);
  useEffect(() => {
    if (import.meta.env.VITE_MODE === 'local' && needsDefaultPasswordChange) {
      setShowChangePasswordModal(true);
    }
  }, [needsDefaultPasswordChange]);
  return (
    <NotificationContext.Provider value={{ visitorList, setVisitorList }}>
      <AppShell
        header={isMobile ? { height: 70 } : undefined}
        navbar={{
          width: 280,
          breakpoint: 'sm',
          collapsed: { mobile: !navbarOpened },
        }}
        padding="md"
        styles={{
          main: {
            background: '#FAFBFF',
          },
        }}
        className="overflow-hidden"
      >
        {isMobile && (
          <AppShell.Header>
            <AdminHeader />
          </AppShell.Header>
        )}

        <ChangePasswordModal
          isOpen={showChangePasswordModal}
          onClose={() => setShowChangePasswordModal(false)}
          onSubmit={handlePasswordChange}
        />
        <AppShell.Navbar className={classes.navbar}>
          <Navbar data={combinedNavData} />
        </AppShell.Navbar>
        <AppShell.Main className="pt-0 pb-0 right-container">
          <Container h={'100vh'} className="right-container overflow-y-scroll" fluid>
            {loading ? (
              <FallBack />
            ) : !isLocalMode && assetScope !== null && !companyId && !isOrgBypassRoute ? (
              <EnableOrganizationView
                onOrganizationCreated={() => {
                  fetchCompanyId();
                }}
              />
            ) : isUserDisabledInOrg && !isOrgBypassRoute ? (
              <DisabledUserView />
            ) : (
              children
            )}
          </Container>
        </AppShell.Main>
      </AppShell>
      <Notification />
    </NotificationContext.Provider>
  );
};
